import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Search, Phone, MessageSquare, Navigation, Loader2, Briefcase, Clock } from "lucide-react";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });
const RADII = [2, 5, 10, 15, 25];
const money = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });

// Browser-side lookup via Maps JS Places (works with the referrer-restricted key).
const geocodeInBrowser = (address) =>
  new Promise((resolve) => {
    if (!window.google?.maps?.places) return resolve(null);
    const svc = new window.google.maps.places.PlacesService(document.createElement("div"));
    svc.findPlaceFromQuery({ query: address, fields: ["geometry"] }, (res, status) => {
      const S = window.google.maps.places.PlacesServiceStatus;
      const loc = status === S.OK && res?.[0]?.geometry?.location;
      if (loc) return resolve({ address, lat: loc.lat(), lng: loc.lng() });
      // Only a definite "no such place" is cached as missing; transient errors are retried next search.
      resolve(status === S.ZERO_RESULTS || status === S.NOT_FOUND ? { address, not_found: true } : null);
    });
  });

const geocodeBatch = async (addresses, onProgress) => {
  const out = [];
  for (let i = 0; i < addresses.length; i += 4) {
    out.push(...(await Promise.all(addresses.slice(i, i + 4).map(geocodeInBrowser))));
    onProgress?.(Math.min(i + 4, addresses.length), addresses.length);
  }
  return out;
};

export const outreachText = (name, originDate) => {
  const first = (name || "").trim().split(" ")[0];
  const hi = first && first !== "Unknown" ? `Hi ${first}` : "Hi there";
  if (!originDate || originDate === todayStr()) {
    return `${hi}, we're finishing a job near you today. We could install your screens this afternoon or tomorrow. Want a spot?`;
  }
  const day = new Date(`${originDate}T12:00:00`).toLocaleDateString("en-US", { weekday: "long" });
  return `${hi}, we'll be in your neighborhood on ${day}. Want us to install your screens while we're there?`;
};

function ResultCard({ r, originDate, onText }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-3" data-testid={`nearby-result-${r.lead_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-zinc-900 truncate">{r.name}</div>
          <div className="text-xs text-zinc-500 truncate">{r.address}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold text-[#207024]" data-testid={`nearby-miles-${r.lead_id}`}>{r.miles.toFixed(1)} mi</div>
          <div className="text-xs font-semibold text-zinc-800">{money(r.total)}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-500">
        <span className="px-1.5 py-0.5 rounded bg-zinc-100 capitalize">{r.status.replace(/_/g, " ")}</span>
        {r.days_old != null && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{r.days_old}d old</span>}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <button onClick={() => onText(r, outreachText(r.name, originDate))} disabled={!r.phone}
          className="h-9 rounded-lg bg-[#207024] text-white text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-40"
          data-testid={`nearby-text-${r.lead_id}`}>
          <MessageSquare className="w-3.5 h-3.5" /> Text
        </button>
        <a href={r.phone ? `tel:${r.phone}` : undefined}
          className={`h-9 rounded-lg border border-zinc-300 text-zinc-700 text-xs font-semibold flex items-center justify-center gap-1 ${r.phone ? "" : "opacity-40 pointer-events-none"}`}
          data-testid={`nearby-call-${r.lead_id}`}>
          <Phone className="w-3.5 h-3.5" /> Call
        </a>
        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.address)}`} target="_blank" rel="noreferrer"
          className="h-9 rounded-lg border border-zinc-300 text-zinc-700 text-xs font-semibold flex items-center justify-center gap-1"
          data-testid={`nearby-map-${r.lead_id}`}>
          <Navigation className="w-3.5 h-3.5" /> Map
        </a>
      </div>
    </div>
  );
}

function JobPicker({ jobs, selectedId, onPick }) {
  if (!jobs.length) return null;
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-zinc-500 uppercase">Or start from a scheduled job</div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {jobs.map((j) => (
          <button key={j.job_id} onClick={() => onPick(j)}
            className={`shrink-0 px-3 py-2 rounded-lg border text-left text-xs ${selectedId === j.job_id ? "border-[#207024] bg-green-50" : "border-zinc-200 bg-white"}`}
            data-testid={`nearby-job-${j.job_id}`}>
            <div className="font-semibold text-zinc-800 flex items-center gap-1"><Briefcase className="w-3 h-3" />{j.customer_name || "Job"}</div>
            <div className="text-zinc-500">{j.date === todayStr() ? "Today" : "Tomorrow"}{j.time ? ` · ${j.time}` : ""}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function NearbyEstimates() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [address, setAddress] = useState(params.get("address") || "");
  const [jobId, setJobId] = useState(params.get("job_id") || "");
  const [radius, setRadius] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [jobs, setJobs] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    Promise.all([axios.get(`${API}/jobs`, auth()), axios.get(`${API}/customers`, auth())]).then(([jr, cr]) => {
      const t = todayStr();
      const tm = new Date(Date.now() + 864e5).toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
      const names = Object.fromEntries((cr.data || []).map((c) => [c.customer_id, c.fullName]));
      setJobs((jr.data || [])
        .filter((j) => [t, tm].includes(j.date) && (j.status || "").toLowerCase() !== "completed")
        .map((j) => ({ ...j, customer_name: names[j.customer_id] }))
        .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, { componentRestrictions: { country: "us" }, fields: ["formatted_address"] });
    ac.addListener("place_changed", () => {
      const p = ac.getPlace();
      if (p?.formatted_address) { setAddress(p.formatted_address); setJobId(""); }
    });
  }, []);

  const search = async (opts = {}) => {
    const q = { radius: opts.radius ?? radius };
    const jid = opts.jobId ?? jobId;
    if (jid) q.job_id = jid; else q.address = opts.address ?? address;
    if (!q.job_id && (!q.address || q.address.trim().length < 5)) { toast.error("Enter an address or pick a job"); return; }
    setLoading(true);
    try {
      for (let pass = 0; pass < 4; pass++) {
        const { data: d } = await axios.get(`${API}/estimates/nearby`, { ...auth(), params: q });
        if (!d.needs_geocode?.length) { setData(d); break; }
        setProgress(`Locating ${d.needs_geocode.length} address${d.needs_geocode.length === 1 ? "" : "es"}…`);
        const items = (await geocodeBatch(d.needs_geocode, (n, t) => setProgress(`Locating addresses… ${n}/${t}`))).filter(Boolean);
        if (!items.length) {
          toast.error("Google Maps couldn't look up addresses right now. Try again in a moment.");
          setData(d.origin?.lat == null ? null : d);
          break;
        }
        await axios.post(`${API}/geocode-cache`, { items }, auth());
        setData(d);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  useEffect(() => { if (jobId || address) search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickRadius = (r) => { setRadius(r); if (data) search({ radius: r }); };
  const pickJob = (j) => { setJobId(j.job_id); setAddress(""); search({ jobId: j.job_id }); };
  const openText = (r, text) => {
    const qs = new URLSearchParams({ customer: r.customer_id || "", phone: r.phone || "", name: r.name || "", text });
    navigate(`/messages?${qs.toString()}`);
  };

  const originDate = data?.origin?.date || (jobId ? jobs.find((j) => j.job_id === jobId)?.date : null);

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase">Nearby Estimates</h1>
          <p className="text-xs text-zinc-500">Open quotes close to a job, to fill gaps in today's or tomorrow's schedule.</p>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-3 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input ref={inputRef} value={address} placeholder="Enter an address"
                onChange={(e) => { setAddress(e.target.value); setJobId(""); }}
                onKeyDown={(e) => e.key === "Enter" && search()}
                className="w-full h-11 pl-9 pr-3 rounded-lg border-2 border-zinc-200 focus:border-[#207024] outline-none text-sm"
                data-testid="nearby-address-input" />
            </div>
            <button onClick={() => search()} disabled={loading}
              className="h-11 px-4 rounded-lg bg-[#207024] text-white font-semibold flex items-center gap-1 disabled:opacity-60"
              data-testid="nearby-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>
          <JobPicker jobs={jobs} selectedId={jobId} onPick={pickJob} />
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Within</span>
            {RADII.map((r) => (
              <button key={r} onClick={() => pickRadius(r)}
                className={`h-8 px-3 rounded-full text-xs font-semibold ${radius === r ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`}
                data-testid={`nearby-radius-${r}`}>
                {r} mi
              </button>
            ))}
          </div>
        </div>

        {progress && <div className="text-xs text-zinc-500 flex items-center gap-1" data-testid="nearby-progress"><Loader2 className="w-3 h-3 animate-spin" />{progress}</div>}

        {data && !loading && (
          <div className="space-y-2">
            <div className="text-sm text-zinc-700" data-testid="nearby-summary">
              <span className="font-semibold">{data.results.length}</span> open estimate{data.results.length === 1 ? "" : "s"} within {data.radius} mi of{" "}
              <span className="font-medium">{data.origin.label ? `${data.origin.label}'s job` : data.origin.address}</span>
            </div>
            {data.results.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-200 p-6 text-center text-sm text-zinc-500" data-testid="nearby-empty">
                Nothing that close. Try a bigger radius.
              </div>
            ) : (
              data.results.map((r) => <ResultCard key={r.lead_id} r={r} originDate={originDate} onText={openText} />)
            )}
            {(data.skipped_no_address > 0 || data.skipped_unlocated > 0) && (
              <div className="text-[11px] text-zinc-400">
                {data.skipped_no_address} open estimate(s) have no address{data.skipped_unlocated ? `, ${data.skipped_unlocated} couldn't be located` : ""}.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
